import React, { useState } from 'react'
import { IResponse } from './TemplateMovementsExport.types'
import { useHistory, useParams } from 'react-router-dom';
import { useAlert } from 'contexts/alert';
import { useAuth } from 'contexts/auth';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Button, Chip, Table, TableCell, TableContainer, TableRow, IconButton, FormControl, InputLabel, Select, MenuItem, TextField, Alert, Typography, Box, CircularProgress, Tooltip } from '@mui/material';
import { formatActivityStatus, formatDate, formatMovementType, formatStatus, formatType, particaoDosCriticados, entradasDeRetificacao } from './TemplateMovementsExport.utils';
import { cpfMask } from 'utils/cpf';
import { Metrics, Modal } from 'v2/components';
import { Label, TableLabel, TableText, Title } from './TemplateMovementsExport.styled';
import { Close, EditNote as EditNoteIcon, VerifiedOutlined as VerifiedIcon, ArrowBack, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { ACAO_DEVOLVER, AVISO_CORRIGIDO_SEM_LINHA, AVISO_ERRO_CARREGAR_CADASTRO, MOTIVO_GENERICO_DEVOLVER_FILA, erroAoDevolverAFila, AVISO_CORRIGIDO_VOLTOU, AVISO_SEM_CRITICA_A_TRATAR, resumoCorrecaoMista, DICA_MARCACOES_NAO_SALVAS, MOTIVOS_DA_CONFERENCIA, ROTULO_CRITICADO, ROTULO_RETIDO, acaoDaLinha, acaoTratarCorrecoes, avisoMarcacoesNaoSalvas, resumoDasMarcacoesSalvas, textosDoModal } from '../retencao-vocabulario';
import { RetencaoAviso } from '../RetencaoAviso';
import { AppEditDataTable } from '../AppEditData/AppEditData.Table';
import { IMassaTotal } from '../AppEditData/AppEditData.types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ptBR } from '@mui/x-data-grid/locales';
import api from 'services/api';
import dayjs from 'dayjs';

export function TemplateMovementsExportView({ data, productType }: { data: IResponse, productType: string }) {
  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string };
  const { openAlert } = useAlert();
  const user = useAuth();
  const history = useHistory();

  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState<"DEFAULT" | "RECTIFY" | "CORRIGIR">("DEFAULT");
  const [isModalActivityOpen, setIsModalActivityOpen] = useState(false);
  const [isModalExportOpen, setIsModalExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isModalRetainReleaseOpen, setIsModalRetainReleaseOpen] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<any>(null);
  const [retentionReason, setRetentionReason] = useState<string>('');
  const [retentionNotes, setRetentionNotes] = useState<string>('');
  const [modifiedRows, setModifiedRows] = useState<Map<string, { retentionReason: string; retentionNotes: string; newStatus: string }>>(new Map());

  // Tratar correções (M3/D5): só os criticados da geração atual, com a partição de cada CPF.
  const editDataActivityId = data.editDataActivityId ?? null;
  const criticados = data.people.filter(p => p.status === 'RETAINED');
  const rectifyWorkspaceActivityId = data.activities.find(a => a.slug === "app_rectify")?.workspaceActivityId;
  const particao = particaoDosCriticados(criticados);
  const massaQuery = useQuery({
    queryKey: ["app_edit_data", editDataActivityId],
    queryFn: () => api.get<{ data: { people: IMassaTotal[]; config: string[] } }>(`/v2/apps/edit-data/${editDataActivityId}`),
    select: (res) => res.data.data,
    enabled: currentStep === "CORRIGIR" && !!editDataActivityId,
  });
  const pessoasACorrigir = (massaQuery.data?.people ?? []).filter((p, i, arr) =>
    particao[p.TAX_PAYER_ID] && arr.findIndex(q => q.TAX_PAYER_ID === p.TAX_PAYER_ID) === i,
  );

  const rows = data.people.map((person, index) => {
    const modification = modifiedRows.get(person.id);
    const currentStatus = modification ? modification.newStatus : person.status;

    return {
      id: person.id || `${index + 1}_${Date.now()}`,
      name: person.name || '-',
      taxPayerId: person.taxPayerId ? cpfMask(person.taxPayerId) : '-',
      taxPayerIdEmployee: person.taxPayerIdEmployee ? cpfMask(person.taxPayerIdEmployee) : '-',
      status: currentStatus,
      movementType: formatMovementType(person.movementType),
      type: formatType(person.type),
      plan: person.plan ?? '-',
      vigencyStartDate: formatDate(String(person.vigencyStartDate)),
      vigencyEndDate: formatDate(String(person.vigencyEndDate)),
      hiringDate: formatDate(String(person.hiringDate)),
      dismissalDate: formatDate(String(person.dismissalDate)),
      startPriorNoticeDate: person.startPriorNoticeDate,
      endPriorNoticeDate: person.endPriorNoticeDate,
      exclusionDateType: person.exclusionDateType,
      effectiveExclusionDate: person.effectiveExclusionDate,
      role: person.role ?? '-',
      city: person.city ?? '-',
      retentionReason: person.retentionReason ?? null,
      retentionNotes: person.retentionNotes ?? null,
    };
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nome Completo',
      width: 300,
      sortable: true,
    },
    {
      field: 'taxPayerId',
      headerName: 'CPF do Beneficiário',
      width: 160,
      sortable: true,
    },
    {
      field: 'taxPayerIdEmployee',
      headerName: 'CPF do Titular',
      width: 160,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 200,
      type: 'singleSelect',
      valueOptions: [
        { value: 'READY', label: 'Pronto' },
        { value: 'SKIPED', label: ROTULO_RETIDO },
        { value: 'DELAYED', label: 'Em espera' },
        { value: 'RETAINED', label: ROTULO_CRITICADO },
      ], // {value, label} pairs
      renderCell: (params: GridRenderCellParams) => {
        const { label, backgroundColor, color } = formatStatus(params.row.status);
        return (
          <Chip
            label={label}
            size="small"
            sx={{ backgroundColor: backgroundColor, color: color, fontWeight: 500 }}
          />
        );
      },
    },
    {
      field: 'movementType',
      headerName: 'Tipo de movimentação',
      width: 160,
      sortable: true,
    },
    {
      field: 'type',
      headerName: 'Tipo do Beneficiário',
      width: 150,
      sortable: true,
    },
    {
      field: 'plan',
      headerName: 'Plano',
      width: 200,
      sortable: true,
    },
    {
      field: 'vigencyStartDate',
      headerName: 'Data inicio plano',
      width: 180,
      sortable: true,
    },
    {
      field: 'vigencyEndDate',
      headerName: 'Data fim plano',
      width: 180,
      sortable: true,
    },
    {
      field: 'hiringDate',
      headerName: 'Data de admissão',
      width: 180,
      sortable: true,
    },
    {
      field: 'dismissalDate',
      headerName: 'Data de demissão',
      width: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value || params.value === '-') return '-';
        const isUsed = params.row.exclusionDateType === 'DISMISSAL_DATE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {params.value}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'endPriorNoticeDate',
      headerName: 'Término Aviso Prévio',
      width: 220,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.endPriorNoticeDate) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        const isUsed = params.row.exclusionDateType === 'PRIOR_NOTICE_END_DATE';
        const formattedDate = formatDate(String(params.row.endPriorNoticeDate));
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {formattedDate}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'role',
      headerName: 'Cargo',
      width: 180,
      sortable: true,
    },
    {
      field: 'city',
      headerName: 'Cidade',
      width: 180,
      sortable: true,
    },
    {
      field: "retainRelease",
      headerName: ACAO_DEVOLVER,
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isRetained = params.row.status === 'RETAINED';
        const acao = acaoDaLinha(params.row.status, "EXPORTACAO");

        return (
          <Tooltip title={acao}>
            <IconButton
              size="small"
              aria-label={acao}
              onClick={() => handleOpenRetainReleaseModal(params.row)}
              sx={{
                color: isRetained ? '#2E7D32' : '#0F0F1E'
              }}
            >
              {isRetained ? (
                <VerifiedIcon />
              ) : (
                <EditNoteIcon />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  async function handleExport() {
    try {
      setIsExporting(true);

      const beingExported = [ ...data.people ];
      const metrics = new Array();
      metrics.push({ label: "Total", value: String(beingExported?.length ?? 0) });
      metrics.push({ label: "Inclusões", value: String(beingExported?.filter(p => p?.movementType === "inclusion")?.length ?? 0) });
      metrics.push({ label: "Exclusões", value: String(beingExported?.filter(p => p?.movementType === "exclusion")?.length ?? 0) });
      metrics.push({ label: "Alterações", value: String(beingExported?.filter(p => p?.movementType === "change")?.length ?? 0) });
      metrics.push({ label: "Transferências", value: String(beingExported?.filter(p => p?.movementType === "transfer")?.length ?? 0) });

      const exportWorkspaceActivityId = data.activities.find(a => a.slug === "app_export")?.workspaceActivityId ?? null;

      if (!exportWorkspaceActivityId) throw new Error("Workspace Activity ID de exportação é obrigatório.");

      await api.post(`/v2/export/${exportWorkspaceActivityId}`);

      openAlert('Massa movimentada exportada com sucesso.', 'success');
      setIsModalExportOpen(false);
    } catch (error: any) {
      // O backend devolve mensagens acionáveis aqui — a guarda de contingência
      // diz quantos registros aguardam conclusão manual e onde resolver, e a
      // checagem de entrega diz que o e-mail não chegou a ninguém. Trocar isso
      // por "Erro ao exportar" apaga justamente a instrução que o operador
      // precisa.
      const backendMessage = error?.response?.data?.data?.message;
      if (backendMessage) return openAlert(backendMessage, 'error');
      if (error instanceof Error && !!error.message?.length) {
        return openAlert(error.message, 'error');
      }
      openAlert('Erro ao exportar massa movimentada.', 'error');
    } finally {
      setIsExporting(false);
    }
  }

  const handleOpenRetainReleaseModal = (beneficiary: any) => {
    setSelectedBeneficiary(beneficiary);

    // Se o beneficiário está RETAINED, buscar informações salvas.
    // Prioriza modificações em memória (edição não-salva); se não houver, usa
    // os dados persistidos no próprio beneficiário (gravados pelo backend, ex: rejeições da API QBem).
    const isRetained = beneficiary?.status === 'RETAINED';
    const modification = modifiedRows.get(beneficiary.id);

    if (isRetained) {
      setRetentionReason(modification?.retentionReason ?? beneficiary?.retentionReason ?? '');
      setRetentionNotes(modification?.retentionNotes ?? beneficiary?.retentionNotes ?? '');
    } else {
      setRetentionReason('');
      setRetentionNotes('');
    }
    setIsModalRetainReleaseOpen(true);
  };

  const handleCloseRetainReleaseModal = () => {
    setIsModalRetainReleaseOpen(false);
    setSelectedBeneficiary(null);
    setRetentionReason('');
    setRetentionNotes('');
  };

  const handleConfirmRetainRelease = () => {
    const beneficiaryId = selectedBeneficiary?.id;
    if (!beneficiaryId) return;

    const isRetained = selectedBeneficiary.status === 'RETAINED';

    // Atualizar o mapa de linhas modificadas
    setModifiedRows(prev => {
      const newMap = new Map(prev);

      if (isRetained) {
        // Marcar como corrigido: mudar status para DONE e limpar informações
        newMap.set(beneficiaryId, {
          retentionReason: '',
          retentionNotes: '',
          newStatus: 'DONE'
        });
      } else {
        // Devolver para correção: mudar status para RETAINED e salvar informações
        newMap.set(beneficiaryId, {
          retentionReason,
          retentionNotes,
          newStatus: 'RETAINED'
        });
      }

      return newMap;
    });

    // Fechar modal
    handleCloseRetainReleaseModal();
  };

  const handleClearAll = () => {
    // Limpar todas as modificações
    setModifiedRows(new Map());
  };

  const handleSubmitRectifications = async () => {
    try {
      if (modifiedRows.size === 0) {
        openAlert('Nenhuma marcação para salvar.', 'warning');
        return;
      }

      setIsExporting(true);

      // Preparar payload com as modificações
      const payload = Array.from(modifiedRows.entries()).map(([beneficiaryId, modification]) => {
        const person = data.people.find(p => p.id === beneficiaryId);

        if (!person) return null;

        return {
          dagRunId: person.dagRunId,
          taxPayerId: person.taxPayerId,
          status: modification.newStatus,
          retentionReason: modification?.retentionReason || null,
          retentionNotes: modification?.retentionNotes || null,
        };
      }).filter(item => item !== null);

      const rectifyWorkspaceActivityId = data.activities.find(a => a.slug === "app_rectify")?.workspaceActivityId;
      await api.post(`/v2/apps/rectification/${rectifyWorkspaceActivityId}`, { payload });

      // O save só grava o status na massa: nada é enviado à operadora nem ao RH.
      const mudancas = Array.from(modifiedRows.entries()).map(([beneficiaryId, modification]) => ({
        antes: data.people.find(p => p.id === beneficiaryId)?.status,
        depois: modification.newStatus,
      }));
      const devolvidos = mudancas.filter(m => m.depois === 'RETAINED').length;
      const corrigidos = mudancas.filter(m => m.depois === 'DONE' && m.antes === 'RETAINED').length;
      openAlert(resumoDasMarcacoesSalvas(devolvidos, corrigidos), 'success');
      window.location.reload()
    } catch (error: any) {
      // O backend diz quantos registros não casaram e por quê; a mensagem genérica apagava isso.
      const backendMessage = error?.response?.data?.data?.message;
      openAlert(backendMessage || 'Erro ao salvar as marcações.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSalvoNaEdicao = async (resultado?: { pessoas: { taxPayerId: string; linhasNaMassa: number | null }[] }) => {
    const pessoas = resultado?.pessoas ?? [];
    const comLinha = pessoas.filter(p => (p.linhasNaMassa ?? 0) >= 1);
    const semLinha = pessoas.length - comLinha.length;
    if (comLinha.length === 0) {
      if (semLinha > 0) openAlert(AVISO_CORRIGIDO_SEM_LINHA, 'warning');
      return;
    }
    try {
      // why: uma entrada por linha criticada, cada uma com o movementType real (par de transferencia vai inteiro).
      const payload = entradasDeRetificacao(criticados, comLinha);
      await api.post(`/v2/apps/rectification/${rectifyWorkspaceActivityId}`, { payload });
      if (semLinha > 0) openAlert(resumoCorrecaoMista(comLinha.length, semLinha), 'warning');
      else openAlert(AVISO_CORRIGIDO_VOLTOU, 'success');
      await queryClient.invalidateQueries({ queryKey: ["movements-export", workspaceId, templateId] });
    } catch (error: any) {
      const backendMessage = error?.response?.data?.data?.message;
      openAlert(erroAoDevolverAFila(backendMessage || MOTIVO_GENERICO_DEVOLVER_FILA), 'error');
    }
  };

  return (
    <>
      {currentStep === "CORRIGIR" && (
        <>
          <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center" }}>
            <IconButton size="small" aria-label="Voltar" onClick={() => setCurrentStep("DEFAULT")}>
              <ArrowBack color="primary" />
            </IconButton>
            <Title>{acaoTratarCorrecoes(criticados.length)}</Title>
          </div>
          {massaQuery.isLoading && <CircularProgress size="20px" />}
          {massaQuery.isError && <Alert severity="error">{AVISO_ERRO_CARREGAR_CADASTRO}</Alert>}
          {massaQuery.data && pessoasACorrigir.length === 0 && (
            <Typography variant="body1">{AVISO_SEM_CRITICA_A_TRATAR}</Typography>
          )}
          {massaQuery.data && pessoasACorrigir.length > 0 && (
            <AppEditDataTable
              people={pessoasACorrigir}
              editableFields={data.editableFields ?? []}
              workspaceActivityId={editDataActivityId ?? undefined}
              partição={particao}
              onSalvo={handleSalvoNaEdicao}
            />
          )}
        </>
      )}
      {currentStep === "DEFAULT" && (
        <>
          <Modal open={isModalActivityOpen} size="lg">
            <div style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Title>Atividades relacionadas</Title>
              <Close
                style={{ cursor: "pointer" }}
                onClick={() => setIsModalActivityOpen(false)}
              />
            </div>
            <TableText>
              Para ter Exportar Movimentações atualizada verifique se as
              atividades abaixo estão atualizadas de acordo com sua execução
              programada.
            </TableText>
            <div style={{ width: "100%", maxHeight: "400px", overflow: "scroll" }}>
              <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
                <Table>
                  <TableRow style={{ height: "70px" }}>
                    <TableCell>
                      <TableLabel>Nome</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Última atualização</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Execução programada</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Status</TableLabel>
                    </TableCell>
                  </TableRow>
                  {data.activities.map(activity => (
                    <TableRow key={activity.slug} style={{ height: "70px" }}>
                      <TableCell>{activity.name}</TableCell>
                      <TableCell>{formatDate(String(activity.lastExecution), "timestamp_2")}</TableCell>
                      <TableCell>{formatDate(String(activity.scheduledExecution), "timestamp_2")}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatActivityStatus(activity.status).label}
                          size="small"
                          sx={{ backgroundColor: formatActivityStatus(activity.status).backgroundColor, color: formatActivityStatus(activity.status).color, fontWeight: "500", opacity: "0.8" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </Table>
              </TableContainer>
            </div>
          </Modal>
          <Modal open={isModalExportOpen} size="lg">
            <div style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Title>Exportar movimentações?</Title>
              <Close
                style={{ cursor: "pointer" }}
                onClick={() => setIsModalExportOpen(false)}
              />
            </div>
            <TableText>
              Você está prestes a exportar as movimentações
            </TableText>
            {modifiedRows.size > 0 && (
              <Alert severity="warning" style={{ marginTop: "12px", marginBottom: "4px" }}>
                {avisoMarcacoesNaoSalvas(modifiedRows.size)}
              </Alert>
            )}
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
              <Button
                variant="outlined"
                color="error"
                style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                disabled={isExporting}
                onClick={() => setIsModalExportOpen(false)}
              >
                Voltar para revisão
              </Button>
              <Button
                variant='contained'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                disabled={isExporting || modifiedRows.size > 0}
                onClick={handleExport}
              >
                Exportar
              </Button>
            </div>
          </Modal>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title>Exportar movimentações</Title>
            <Button
              variant="outlined"
              style={{ padding: "1px 16px 1px 16px", fontSize: "14px", fontWeight: "600" }}
              onClick={() => setIsModalActivityOpen(true)}
            >
              Atividades relacionadas
            </Button>
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "row", gap: "6px", alignItems: "center" }}>
              <Label>Última aprovação em</Label>
              <Label>
                <strong>{formatDate(String(data.lastExecution), "timestamp")}</strong>
              </Label>
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: "6px", alignItems: "center" }}>
              <Label>Status</Label>
              <Chip
                label={formatActivityStatus(data.status).label}
                size="small"
                sx={{ backgroundColor: formatActivityStatus(data.status).backgroundColor, color: formatActivityStatus(data.status).color, fontWeight: "500", opacity: "0.8" }}
              />
            </div>
          </div>
          <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "16px" }}>
            <Metrics title="Movimentações" metrics={data.metricsMovement.map(m => m.label === "Total" ? { ...m, bold: true } : m)} />
            <Metrics title="Detalhamento por status" metrics={data.metricsStatus.map(m => m.label === "Total" ? { ...m, bold: true } : m)} />
          </div>
          <DataGrid
            rows={rows}
            columns={columns.filter(c => c?.field !== "retainRelease")}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 5 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            style={{ backgroundColor: "white", minHeight: 400 }}
          />
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
            {criticados.length > 0 && editDataActivityId && rectifyWorkspaceActivityId && (
              <Button
                variant='outlined'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={() => setCurrentStep("CORRIGIR")}
              >
                {acaoTratarCorrecoes(criticados.length)}
              </Button>
            )}
            <Button
              variant='outlined'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setCurrentStep("RECTIFY")}
            >
              {ACAO_DEVOLVER}
            </Button>
            <Tooltip title={modifiedRows.size > 0 ? DICA_MARCACOES_NAO_SALVAS : ''}>
              <span>
                <Button
                  variant='contained'
                  style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                  disabled={modifiedRows.size > 0}
                  onClick={() => setIsModalExportOpen(true)}
                >
                  Exportar movimentações
                </Button>
              </span>
            </Tooltip>
          </div>
        </>
      )}
      {currentStep === "RECTIFY" && (
        <>
          <Modal open={isModalRetainReleaseOpen} size="lg">
            <Box sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Title>{textosDoModal(selectedBeneficiary?.status, "EXPORTACAO").titulo}</Title>
                <Close
                  style={{ cursor: "pointer" }}
                  onClick={handleCloseRetainReleaseModal}
                />
              </div>
              <Typography variant="body1" style={{ marginBottom: "20px", marginTop: "5px" }}>
                {textosDoModal(selectedBeneficiary?.status, "EXPORTACAO").explicacao}
              </Typography>
              <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'row', justifyContent: "space-between", marginBottom: "20px" }}>
                <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
                  <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Nome</p>
                  <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.name}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
                  <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CPF do beneficiário</p>
                  <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.taxPayerId}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
                  <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CPF do titular</p>
                  <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.taxPayerIdEmployee}</span>
                </div>
              </div>
              <RetencaoAviso status={selectedBeneficiary?.status} contexto="EXPORTACAO" />

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="retention-reason-label">Motivo</InputLabel>
                <Select
                  labelId="retention-reason-label"
                  value={retentionReason}
                  label="Motivo"
                  onChange={(e) => setRetentionReason(e.target.value)}
                  disabled={selectedBeneficiary?.status === 'RETAINED'}
                >
                  {MOTIVOS_DA_CONFERENCIA.map((motivo) => (
                    <MenuItem key={motivo} value={motivo}>{motivo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                label="Observações"
                value={retentionNotes}
                onChange={(e) => setRetentionNotes(e.target.value)}
                disabled={selectedBeneficiary?.status === 'RETAINED'}
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': {
                    height: '240px',
                    alignItems: 'flex-start',
                  }
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant='outlined'
                  color='error'
                  style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                  onClick={handleCloseRetainReleaseModal}
                >
                  Cancelar
                </Button>

                <Button
                  variant='contained'
                  style={{ minWidth: "180px", paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                  disabled={
                    // Se está marcando como corrigido (RETAINED ou SKIPED), não desabilita
                    // Se está retendo (status diferente), desabilita se não tiver motivo OU observação
                    !(selectedBeneficiary?.status === 'RETAINED' || selectedBeneficiary?.status === 'SKIPED') &&
                    (!retentionReason.trim() || !retentionNotes.trim())
                  }
                  onClick={handleConfirmRetainRelease}
                >
                  {textosDoModal(selectedBeneficiary?.status, "EXPORTACAO").botao}
                </Button>
              </Box>
            </Box>
          </Modal>
          <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={() => {
                // A decisão em `modifiedRows` só existe em memória até
                // "Salvar" nesta etapa (handleSubmitRectifications). Sair
                // sem salvar não pode deixar essa decisão fantasma viva
                // quando o operador voltar para a exportação (D16).
                setModifiedRows(new Map());
                setCurrentStep("DEFAULT");
              }}
            >
              <ArrowBack color="primary" />
            </IconButton>
            <Title>{ACAO_DEVOLVER}</Title>
          </div>
          <div style={{"display":"flex","gap":"1rem"}}>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Total</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Inclusões</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.filter(p => p?.movementType === "inclusion")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Exclusões</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.filter(p => p?.movementType === "exclusion")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Alterações</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.filter(p => p?.movementType === "change")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Transferências</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.filter(p => p?.movementType === "transfer")?.length ?? 0}</span>
            </div>
          </div>
          <DataGrid
            rows={rows.filter(r => r.status === "DONE" || r.status === "RETAINED")}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 5 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            style={{ backgroundColor: "white", minHeight: 400 }}
          />
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
            <Button
              variant="outlined"
              style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
              onClick={handleClearAll}
              disabled={modifiedRows.size === 0}
            >
              Limpar
            </Button>
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={handleSubmitRectifications}
              disabled={isExporting || modifiedRows.size === 0}
            >
              {!isExporting ? "Salvar" : <CircularProgress size="20px"/>}
            </Button>
          </div>
        </>
      )}
    </>
  )
}
